/**
 * The claim catalogue and the seeded system roles (IP-46, spec 6.1).
 *
 * A claim is a Core permission code, fixed here in code because the server
 * enforces it; migration 0006 seeds console_claim from the same list, and a
 * new claim arrives with the migration that ships the code enforcing it. A
 * role is DATA (console_role_def + console_role_claim) that an admin builds
 * from these claims; SYSTEM_ROLES are the eight roles 0006 seeds, so the dev
 * users keep working. A person's permissions are the union of their roles'
 * claims.
 *
 * View claims decide what a person sees; action claims decide what they may
 * do, and an action needs its claim AND its class (catalogue.js). A claim
 * marked externalSafe is one a person outside Lynear (the Trustee) may hold;
 * IP-65's LIV trustee gate went with LIV sign-in (IP-72), and the flag stays
 * as the catalogue's record of which claims are safe to grant outside.
 *
 * (IP-67..71) core.cash.claim also grants LINE_RETURN and core.payout.manage
 * RECEIPT_REFUND, and (Ruling R16, Task 8 review) core.holder.approve also
 * grants HOLDER_REJECT. The descriptions below are spec 6.1's as amended; 0006
 * seeded the earlier ones, and migration 0007 brings console_claim's
 * descriptions up to these.
 *
 * (IP-85, IP-86) core.order.approve also grants the checker's ORDER_SEND_BACK and
 * ORDER_REJECT - no new claim, so nothing new for ADMIN to hold (IP-81);
 * migration 0010 rewrites its description to this one.
 *
 * (IP-102) core.fees.propose and core.fees.approve, on the Settings screen:
 * a fund's fees are proposed by one person and approved or rejected by
 * another, each with a fresh code (IP-101). Migration 0011 adds both and
 * grants them to ADMIN (IP-81); no other system role holds them.
 *
 * (IP-103) core.intake.initial, on File Uploads: an INITIAL load - the Core
 * initialised from data an admin already holds - needs it on top of the
 * intake claims, to upload and again to accept (intake/registry.js's
 * uploadClaims and acceptClaims). Not external-safe. Migration 0012 adds it
 * and grants it to ADMIN (IP-81); no other system role holds it.
 *
 * (IP-142..IP-156) core.feeds.send, on Business Day: re-send a day's analytics
 * files (FEEDS_RESEND), or record that a day has no wealth-managed holdings
 * (FEEDS_WM_WAIVE). Migration 0028 adds it and grants it to SUPERVISOR and ADMIN.
 */

const claim = (code, screen, description, externalSafe = false) => Object.freeze({ claim: code, screen, description, externalSafe });

/** Spec 6.1's claims, in its order: {claim, screen, description, externalSafe}. */
export const CLAIMS = Object.freeze([
  claim('core.uploads.view', 'File Uploads', 'See batches'),
  claim('core.intake.upload', 'File Uploads', 'INTAKE_UPLOAD'),
  claim('core.intake.accept', 'File Uploads', 'INTAKE_ACCEPT, INTAKE_REJECT'),
  claim('core.intake.initial', 'File Uploads', 'INTAKE_UPLOAD and INTAKE_ACCEPT of an INITIAL load'),
  claim('core.prices.view', 'Unit Prices', 'See runs and prices', true),
  claim('core.price.override', 'Unit Prices', 'PRICE_OVERRIDE'),
  claim('core.price.approve', 'Unit Prices', 'PRICE_APPROVE, PRICE_REJECT'),
  claim('core.dealing.view', 'Transactions & Approvals', 'See transactions, books, the statement, payouts'),
  claim('core.cash.claim', 'Transactions & Approvals', 'MATCH_CLAIM, LINE_CLEAR, LINE_RETURN'),
  claim('core.cash.approve', 'Transactions & Approvals', 'MATCH_APPROVE, MATCH_REJECT, LINE_UNRECONCILE, LINE_MANUAL_CLEAR'),
  claim('core.book.close', 'Transactions & Approvals', 'BOOK_CLOSE'),
  claim('core.order.review', 'Transactions & Approvals', 'ORDER_REVIEW'),
  claim('core.order.approve', 'Transactions & Approvals', 'ORDER_APPROVE, ORDER_SEND_BACK, ORDER_REJECT'),
  claim('core.order.manage', 'Transactions & Approvals', 'ORDER_RELEASE, ORDER_REJECT_HOLD, ORDER_CANCEL'),
  claim('core.payout.manage', 'Transactions & Approvals', 'PAYOUT_GENERATE, PAYOUT_SUBMIT, PAYOUT_RETURN, PAYOUT_CONFIRM, RECEIPT_REFUND, file download'),
  claim('core.registry.view', 'Registry', 'See the register', true),
  claim('core.registry.extract', 'Registry', 'The Rule 37(3) extract'),
  claim('core.reports.view', 'Reports', 'Run reports', true),
  claim('core.clients.view', 'Clients', 'See holders'),
  claim('core.holder.propose', 'Clients', 'HOLDER_PROPOSE, HOLDER_STATUS'),
  claim('core.holder.approve', 'Clients', 'HOLDER_APPROVE, HOLDER_REJECT'),
  claim('core.fees.propose', 'Settings', 'FUND_FEES_PROPOSE'),
  claim('core.fees.approve', 'Settings', 'FUND_FEES_APPROVE, FUND_FEES_REJECT'),
  claim('core.feeds.send', 'Transactions & Approvals', 'FEEDS_RESEND, FEEDS_WM_WAIVE'),
  claim('core.access.admin', 'Access', 'USER_INVITE, USER_RESEND_INVITE, USER_SUSPEND, USER_REINSTATE, USER_ROLES_SET, ROLE_SAVE, ROLE_RETIRE, ROLE_ASSIGN, ROLE_REMOVE, MFA_RESET'),
]);

const BY_CODE = new Map(CLAIMS.map((c) => [c.claim, c]));

/**
 * What each claim means, in words an administrator choosing a role's claims can
 * act on: a short name, and one sentence on what it lets a person do. Display
 * only - the code is what the server enforces - and kept here, beside the codes,
 * so a new claim cannot arrive without its words (catalogue.test.js checks it).
 */
export const CLAIM_TEXT = Object.freeze({
  'core.uploads.view': { label: 'View uploaded files', help: 'See the File Uploads list - each file, its state, its rows and anything wrong with them.' },
  'core.intake.upload': { label: 'Upload files', help: 'Upload NAV value and bank statement files. Each upload asks for an authenticator code.' },
  'core.intake.accept': { label: 'Accept or reject uploaded files', help: 'Accept a checked file so it is used, or reject it with a reason. Never a file they uploaded themselves.' },
  'core.intake.initial': {
    label: 'Initialise the Core from files',
    help: 'Upload, or accept, an initial load - calendar, price history, opening holdings or transaction history - alongside the upload or accept permission. Each asks for an authenticator code.',
  },
  'core.prices.view': { label: 'View unit prices', help: 'See the NAV runs, the checks on them, and the published unit prices.' },
  'core.price.override': { label: 'Override a price check', help: 'Accept a day-on-day price movement outside its normal band, with a written reason.' },
  'core.price.approve': {
    label: 'Approve or reject prices',
    help: 'Approve a NAV run as its checker, which publishes the unit price, or reject it. Never a run they prepared or overrode.',
  },
  'core.dealing.view': { label: 'View transactions, cash and payouts', help: 'See transactions, dealing books, bank statement lines and payouts.' },
  'core.cash.claim': { label: 'Match and clear bank payments', help: 'Match a bank credit to a transaction, record when a cheque cleared, or record a returned cheque.' },
  'core.cash.approve': { label: 'Approve payment matches', help: 'Approve or reject a payment match someone else proposed, undo an automatic match that has not been dealt yet, or clear a left-over bank statement line by hand.' },
  'core.book.close': { label: 'Close a dealing book', help: 'Close a fund\'s book for a dealing day by hand, once its cut-off has passed.' },
  'core.order.review': {
    label: 'Review transactions',
    help: 'Mark priced transactions as reviewed and set each subscription\'s front-end fee - the first of the two approvals before units are allocated.',
  },
  'core.order.approve': {
    label: 'Approve transactions',
    help: 'Give the second approval, which allocates the units, or send a transaction back for a new review, or reject it. Never transactions they reviewed.',
  },
  'core.order.manage': { label: 'Release, reject or cancel transactions', help: 'Release or reject a transaction on hold, or cancel a transaction before it is dealt.' },
  'core.payout.manage': { label: 'Manage payouts and refunds', help: 'Create, download and submit payout files, record payments returned or confirmed, and refund money nobody could match.' },
  'core.registry.view': { label: 'View the unit register', help: 'See holdings, lots and unit transactions by fund and holder.' },
  'core.registry.extract': { label: 'Download the register extract', help: 'Download the register extract (Rule 37(3)) as a CSV file.' },
  'core.reports.view': { label: 'Run reports', help: 'Open and run the Console\'s reports.' },
  'core.clients.view': { label: 'View clients', help: 'See holders and their details.' },
  'core.holder.propose': { label: 'Propose holders', help: 'Propose a new holder, or change a holder\'s status (active, dormant, closed).' },
  'core.holder.approve': { label: 'Approve holders', help: 'Approve or decline a holder someone else proposed.' },
  'core.fees.propose': {
    label: 'Propose fund fees',
    help: 'Propose a fund\'s exit and front-end fees from a future business day. Each proposal asks for an authenticator code; another person approves it.',
  },
  'core.fees.approve': {
    label: 'Approve fund fees',
    help: 'Approve or reject fund fees someone else proposed. Each decision asks for an authenticator code. Never their own proposal.',
  },
  'core.feeds.send': {
    label: 'Send the analytics feeds',
    help: "Re-send a business day's analytics files, or record that a day has no wealth-managed holdings.",
  },
  'core.access.admin': { label: 'Manage users and roles', help: 'Invite users, change their roles, reset authenticators, suspend and reinstate users, and edit roles. Never on their own account.' },
});

/** The Console section each group of claims belongs to, as the navigation names it. */
export const CLAIM_GROUP_LABEL = Object.freeze({
  'File Uploads': 'File Uploads',
  'Unit Prices': 'Unit Prices',
  'Transactions & Approvals': 'Transaction Approval',
  Registry: 'Fund Register',
  Reports: 'Reports',
  Clients: 'Clients',
  Access: 'User Management',
  Settings: 'Settings',
});

/** A claim's words - its label and help - with the code itself as the last resort. */
export function claimText(code) {
  const text = CLAIM_TEXT[code];
  return text ? { ...text } : { label: String(code), help: '' };
}

/** The six view claims ("every view claim" in spec 6.1's role table). */
export const VIEW_CLAIMS = Object.freeze([
  'core.uploads.view', 'core.prices.view', 'core.dealing.view', 'core.registry.view', 'core.reports.view', 'core.clients.view',
]);

/**
 * Spec 6.1's seeded system roles and their claims, as migration 0006 seeds
 * them less core.price.verify, which migration 0016 withdrew and removed (the
 * Trustee's verification is gone: one approval publishes a price).
 */
export const SYSTEM_ROLES = Object.freeze({
  OPERATOR: Object.freeze(['core.uploads.view', 'core.intake.upload', 'core.prices.view', 'core.dealing.view', 'core.cash.claim',
    'core.order.review', 'core.payout.manage', 'core.registry.view', 'core.registry.extract', 'core.reports.view',
    'core.clients.view', 'core.holder.propose']),
  SUPERVISOR: Object.freeze([...VIEW_CLAIMS, 'core.intake.accept', 'core.price.approve', 'core.cash.claim', 'core.cash.approve',
    'core.book.close', 'core.order.review', 'core.order.approve', 'core.order.manage', 'core.payout.manage',
    'core.registry.extract', 'core.holder.approve', 'core.feeds.send']),
  PRICING: Object.freeze(['core.uploads.view', 'core.intake.upload', 'core.prices.view', 'core.registry.view', 'core.reports.view']),
  COMPLIANCE: Object.freeze([...VIEW_CLAIMS, 'core.price.override', 'core.order.manage', 'core.registry.extract']),
  MLRO: Object.freeze([...VIEW_CLAIMS, 'core.registry.extract']),
  TRUSTEE: Object.freeze(['core.prices.view', 'core.registry.view']),
  READONLY: Object.freeze([...VIEW_CLAIMS]),
  // IP-81 (migration 0009): every claim. Four eyes still hold per row.
  ADMIN: Object.freeze(CLAIMS.map((c) => c.claim)),
});

/** True for a claim code this build knows. */
export function isClaim(code) {
  return typeof code === 'string' && BY_CODE.has(code);
}

/**
 * IP-65: true only when every claim is a known, external-safe claim. An empty
 * set is safe (it grants nothing); an unknown code never is.
 */
export function externallySafe(claims) {
  return (claims ?? []).every((code) => BY_CODE.get(code)?.externalSafe === true);
}

/** The screens the claims open, each once, in catalogue order (unknown codes open nothing). */
export function screensFor(claims) {
  const held = new Set(claims ?? []);
  const screens = [];
  for (const c of CLAIMS) {
    if (held.has(c.claim) && !screens.includes(c.screen)) screens.push(c.screen);
  }
  return screens;
}
